import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { AuthStore } from '@stores/auth.store';
import { TenantStore } from '@stores/tenant.store';
import { LoggerService } from '../services/logger.service';
import { sanitizeUrl } from '../utils/url-sanitizer';
import { apiRequestUrl, isTenantNeutralRequest } from './api-request-policy';
import { AuthStoreSignals, TenantStoreSignals } from '../models/store-interfaces.model';

/**
 * Multi-Tenant Interceptor
 *
 * Automatically adds X-Tenant-Id header to all outgoing requests based on the
 * currently authenticated user's tenant. This ensures all API calls are properly
 * scoped to the user's tenant context.
 *
 * Features:
 * - Automatic tenant ID injection
 * - Skip for authentication endpoints
 * - Support for system admin override
 * - Header validation and sanitization
 */
export const tenantInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore) as unknown as AuthStoreSignals;
  const tenantStore = inject(TenantStore) as unknown as TenantStoreSignals;
  const logger = inject(LoggerService);

  const url = apiRequestUrl(req.url);
  // An older active worker may still have the removed API metadata group.
  // Bypass it before auth/tenant routing; HTTP cache-control alone does not
  // bypass Angular's worker. Only same-origin requests need this header:
  // cross-origin API servers are outside that group and keep their CORS contract.
  if (url && globalThis.location &&
      new URL(req.url, globalThis.location.href).origin === globalThis.location.origin) {
    req = req.clone({ setHeaders: { 'ngsw-bypass': 'true' } });
  }
  if (!url || !url.pathname.startsWith('/api/v1/') ||
      isTenantNeutralRequest(req.method, url.pathname)) {
    return next(req);
  }
  
  // Get tenant ID from TenantStore first, fallback to user's default tenant
  const currentTenant = tenantStore.currentTenant();
  const user = authStore.user();
  
  let tenantId = currentTenant?.id || user?.tenantId;

  if (!tenantId) {
    logger.warn('[TenantInterceptor] No tenant context available', {
      userId: user?.id,
      currentTenant: currentTenant,
      url: sanitizeUrl(req.url)
    });
    // Reject the request instead of letting it through to prevent data corruption.
    // sanitize req.url because the HttpErrorResponse propagates to error
    // interceptors and Sentry, where the URL field is logged unredacted.
    return throwError(() => new HttpErrorResponse({
      error: { message: 'No tenant context available. Please select a tenant or contact support.' },
      status: 400,
      statusText: 'Bad Request',
      url: sanitizeUrl(req.url)
    }));
  }

  const tenantIdRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!tenantIdRegex.test(tenantId)) {
    const errorMsg = `[TenantInterceptor] Invalid tenant ID format: "${tenantId}" (type: ${typeof tenantId}, length: ${tenantId?.length})`;
    logger.error(errorMsg, {
      tenantId,
      type: typeof tenantId,
      length: tenantId?.length,
      url: sanitizeUrl(req.url)
    });
    // Reject requests with invalid tenant IDs to prevent security issues.
    // sanitize req.url because the HttpErrorResponse propagates to error
    // interceptors and Sentry, where the URL field is logged unredacted.
    return throwError(() => new HttpErrorResponse({
      error: { message: errorMsg },
      status: 400,
      statusText: 'Bad Request',
      url: sanitizeUrl(req.url)
    }));
  }
  
  // Check if user has system admin role (can bypass tenant restrictions)
  const isSystemAdmin = user?.roles?.includes('system-admin') || false;
  
  // Allow system admins to override tenant via query parameter
  if (isSystemAdmin && req.params.has('tenantId')) {
    const overrideTenantId = req.params.get('tenantId');
    if (overrideTenantId && tenantIdRegex.test(overrideTenantId)) {
      tenantId = overrideTenantId;
    }
  }
  
  // Clone the request and add the tenant header
  const tenantReq = req.clone({
    setHeaders: {
      'X-Tenant-Id': tenantId
    }
  });
  
  return next(tenantReq);
};
